import { useState } from 'react';
import axios from 'axios';
import QRCode from 'react-qr-code';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Award, Phone, Mail, User, CheckCircle, ArrowLeft, Search, QrCode } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LoyaltySignupPage = () => {
  const [mode, setMode] = useState('home'); // home | signup | balance | success | balance_result | qr
  const [form, setForm] = useState({ first_name: '', surname: '', phone: '', email: '', gdpr_consent: false });
  const [balancePhone, setBalancePhone] = useState('');
  const [balanceData, setBalanceData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [successName, setSuccessName] = useState('');

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.first_name || !form.surname || !form.phone || !form.email) {
      setError('All fields are required');
      return;
    }
    if (!form.email.toLowerCase().endsWith('@hlag.com')) {
      setError('Please use your @hlag.com email address');
      return;
    }
    if (!form.gdpr_consent) {
      setError('Please accept the data storage consent to continue');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/loyalty/signup`, form);
      setSuccessName(form.first_name);
      setMode('success');
      setForm({ first_name: '', surname: '', phone: '', email: '', gdpr_consent: false });
    } catch (err) {
      setError(err.response?.data?.detail || 'Signup failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBalanceCheck = async (e) => {
    e.preventDefault();
    setError('');
    if (!balancePhone.trim()) {
      setError('Please enter your phone number');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/loyalty/balance`, { phone: balancePhone.trim() });
      setBalanceData(res.data);
      setMode('balance_result');
    } catch (err) {
      setError(err.response?.data?.detail || 'Account not found');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 to-slate-800 flex items-center justify-center p-4" data-testid="loyalty-signup-page">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/20 mb-4">
            <Award className="w-8 h-8 text-amber-400" />
          </div>
          <h1 className="text-3xl font-bold text-white tracking-tight">Kasseport Loyalty</h1>
          <p className="text-slate-400 mt-2">Earn points on every purchase</p>
        </div>

        {/* Home */}
        {mode === 'home' && (
          <div className="space-y-4">
            <Card className="bg-white/5 border-white/10 backdrop-blur">
              <CardContent className="p-6 space-y-3">
                <div className="text-center space-y-2 mb-4">
                  <p className="text-slate-300 text-sm">
                    Earn <span className="text-amber-400 font-bold">1 point</span> for every £1 spent
                  </p>
                  <p className="text-slate-400 text-xs">
                    100 points = £1 off &bull; 500 points = free meal deal
                  </p>
                </div>
                <Button
                  data-testid="loyalty-join-btn"
                  onClick={() => { setMode('signup'); setError(''); }}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold h-12"
                >
                  <User className="w-4 h-4 mr-2" />
                  Join the Programme
                </Button>
                <Button
                  data-testid="loyalty-balance-btn"
                  onClick={() => { setMode('balance'); setError(''); }}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10 h-12"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Check My Points
                </Button>
                <Button
                  data-testid="loyalty-qr-btn"
                  onClick={() => setMode('qr')}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10 h-10"
                >
                  <QrCode className="w-4 h-4 mr-2" />
                  Show QR Code
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Signup Form */}
        {mode === 'signup' && (
          <Card className="bg-white/5 border-white/10 backdrop-blur">
            <CardHeader className="pb-2">
              <button onClick={() => { setMode('home'); setError(''); }} className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-2">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <CardTitle className="text-white text-xl">Join Loyalty Programme</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSignup} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300 text-sm">First Name</Label>
                    <Input
                      data-testid="signup-first-name"
                      value={form.first_name}
                      onChange={(e) => setForm(f => ({ ...f, first_name: e.target.value }))}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300 text-sm">Surname</Label>
                    <Input
                      data-testid="signup-surname"
                      value={form.surname}
                      onChange={(e) => setForm(f => ({ ...f, surname: e.target.value }))}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                      placeholder="Smith"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Mobile Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      data-testid="signup-phone"
                      value={form.phone}
                      onChange={(e) => setForm(f => ({ ...f, phone: e.target.value }))}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-500 pl-10"
                      placeholder="07700 900 123"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      data-testid="signup-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-500 pl-10"
                      placeholder="name@hlag.com"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Must be your @hlag.com company email</p>
                </div>
                <label className="flex items-start gap-2 text-sm text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="signup-consent"
                    checked={form.gdpr_consent}
                    onChange={(e) => setForm(f => ({ ...f, gdpr_consent: e.target.checked }))}
                    className="rounded border-slate-500 mt-0.5"
                  />
                  <span>I consent to my data being stored for the purpose of the Kasseport loyalty programme. My data will not be shared with third parties.</span>
                </label>
                {error && <p className="text-red-400 text-sm" data-testid="signup-error">{error}</p>}
                <Button
                  type="submit"
                  data-testid="signup-submit"
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold h-12"
                >
                  {loading ? 'Signing up...' : 'Sign Up'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Balance Check */}
        {mode === 'balance' && (
          <Card className="bg-white/5 border-white/10 backdrop-blur">
            <CardHeader className="pb-2">
              <button onClick={() => { setMode('home'); setError(''); }} className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-2">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <CardTitle className="text-white text-xl">Check Points Balance</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleBalanceCheck} className="space-y-4">
                <div>
                  <Label className="text-slate-300 text-sm">Your Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                    <Input
                      data-testid="balance-phone"
                      value={balancePhone}
                      onChange={(e) => setBalancePhone(e.target.value)}
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-500 pl-10"
                      placeholder="07700 900 123"
                      autoFocus
                    />
                  </div>
                </div>
                {error && <p className="text-red-400 text-sm" data-testid="balance-error">{error}</p>}
                <Button
                  type="submit"
                  data-testid="balance-submit"
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold h-12"
                >
                  {loading ? 'Checking...' : 'Check Balance'}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Balance Result */}
        {mode === 'balance_result' && balanceData && (
          <Card className="bg-white/5 border-white/10 backdrop-blur">
            <CardContent className="p-6 text-center">
              <button onClick={() => { setMode('home'); setError(''); }} className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-4">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <p className="text-slate-400 text-sm mb-1">Welcome back,</p>
              <p className="text-white text-xl font-bold mb-6">{balanceData.first_name} {balanceData.surname}</p>
              
              <div className="inline-flex flex-col items-center justify-center w-36 h-36 rounded-full bg-amber-500/20 mb-6">
                <Award className="w-8 h-8 text-amber-400 mb-1" />
                <span className="text-4xl font-bold text-amber-400" data-testid="balance-points">{balanceData.points_balance}</span>
                <span className="text-xs text-amber-300">points</span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="p-3 rounded-lg bg-white/5">
                  <p className="text-xs text-slate-500">Total Earned</p>
                  <p className="text-lg font-bold text-slate-200">{balanceData.points_earned_total}</p>
                </div>
                <div className="p-3 rounded-lg bg-white/5">
                  <p className="text-xs text-slate-500">Redeemed</p>
                  <p className="text-lg font-bold text-slate-200">{balanceData.points_redeemed_total}</p>
                </div>
              </div>

              {balanceData.pending_pence > 0 && (
                <p className="text-xs text-slate-500 mb-2">
                  {balanceData.pending_pence}p accumulating towards your next point
                </p>
              )}
              <p className="text-xs text-slate-600">
                Member since {balanceData.member_since}
              </p>
            </CardContent>
          </Card>
        )}

        {/* QR Code */}
        {mode === 'qr' && (
          <Card className="bg-white/5 border-white/10 backdrop-blur">
            <CardContent className="p-6 text-center">
              <button onClick={() => setMode('home')} className="text-slate-400 hover:text-white text-sm flex items-center gap-1 mb-4">
                <ArrowLeft className="w-3 h-3" /> Back
              </button>
              <p className="text-white text-lg font-semibold mb-4">Scan to Join</p>
              <div className="inline-block bg-white p-4 rounded-xl" data-testid="loyalty-qr-code">
                <QRCode value={window.location.href.split('?')[0]} size={200} />
              </div>
              <p className="text-slate-400 text-sm mt-4">
                Scan this QR code with your phone to sign up for the loyalty programme
              </p>
              <p className="text-slate-500 text-xs mt-2 font-mono">
                {window.location.href.split('?')[0]}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Success */}
        {mode === 'success' && (
          <Card className="bg-white/5 border-white/10 backdrop-blur">
            <CardContent className="p-6 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/20 mb-4">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <h2 className="text-white text-xl font-bold mb-2" data-testid="signup-success">Welcome, {successName}!</h2>
              <p className="text-slate-400 mb-6">
                You've been registered for the Kasseport loyalty programme.
                Start earning points on your next purchase!
              </p>
              <div className="space-y-3">
                <Button
                  onClick={() => { setMode('balance'); setError(''); }}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
                >
                  Check My Points
                </Button>
                <Button
                  onClick={() => setMode('home')}
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-slate-600 mt-6">
          Points expire 31st December each year. See staff for details.
        </p>
      </div>
    </div>
  );
};

export default LoyaltySignupPage;
